// My Observations (/observations) — a teacher's own finalized observation & marking-check records.
// The teacher's copy of the signed paper forms: read, then acknowledge (the signature line).
// Own records only; a draft is invisible until the Lead finalizes it after the 1-on-1 talk.
// (The Lead's working surface for observations lives in Insights — this page is teacher-first,
// but a Lead landing here simply sees the full list, newest first.)

import { api } from "../api";
import { esc } from "../dom";
import { me } from "../state";
import { t } from "../i18n";
import { pageHeader } from "../ui";

interface ObsRow {
  id: string;
  teacher_id: string;
  teacher_name: string;
  kind: "classroom" | "marking";
  observed_on: string;
  class_label: string | null;
  topic: string | null;
  status: "draft" | "finalized" | "acknowledged";
}

const kindLabel = (kind: ObsRow["kind"]): string =>
  kind === "classroom" ? t("obs.kind_classroom") : t("obs.kind_marking");

function chip(status: ObsRow["status"]): string {
  if (status === "draft") return `<span class="chip">${t("common.chip_draft")}</span>`;
  if (status === "finalized") return `<span class="chip submitted">${t("obs.chip_ready")}</span>`;
  return `<span class="chip approved">${t("obs.chip_acknowledged")}</span>`;
}

export async function renderObservations(app: HTMLElement): Promise<void> {
  const isLead = me()?.auth_role === "lead";
  const { observations } = await api<{ observations: ObsRow[] }>("/api/observations");

  const rows = observations.length
    ? observations
        .map((o) => {
          const who = isLead ? `<strong>${esc(o.teacher_name)}</strong> · ` : "";
          const what = `${who}${kindLabel(o.kind)}${o.class_label ? ` · ${esc(o.class_label)}` : ""}`;
          return `<a class="card" data-link href="/observation/${esc(o.id)}">
              ${what}
              <div class="muted" style="font-size:var(--text-sm);margin-top:2px">${esc(o.observed_on)}</div>
              <div style="margin-top:6px">${chip(o.status)}</div>
            </a>`;
        })
        .join("")
    : `<p class="empty">${t("obs.empty")}</p>`;

  app.innerHTML = `
    ${pageHeader({ title: isLead ? t("obs.title_lead") : t("obs.title_teacher"), sub: isLead ? t("obs.hint_lead") : t("obs.hint_teacher") })}
    ${rows}`;
}
